import type {MantineColor} from '@mantine/core';
import type {
  AssistantChartPointResponse,
  AssistantChartResponse,
} from 'src/api/ProactApiClient';
import {
  AssistantChartTypeEnum,
  AssistantChartUnitEnum,
} from 'src/api/ProactApiClient';

// Validated for colorblind separation and contrast; never reorder or cycle.
export const CHAT_CHART_COLORS: readonly MantineColor[] = [
  'blue.7',
  'orange.8',
  'teal.7',
  'grape.7',
  'lime.9',
  'cyan.7',
  'pink.7',
  'indigo.7',
];

export const CHAT_PIE_SIZE = 200;
export const CHART_EMPTY_VALUE = '—';
export const CHART_UNAVAILABLE_TEXT = 'This chart cannot be displayed.';

const CHART_TYPES: readonly AssistantChartTypeEnum[] = [
  AssistantChartTypeEnum.Bar,
  AssistantChartTypeEnum.StackedBar,
  AssistantChartTypeEnum.Line,
  AssistantChartTypeEnum.Area,
  AssistantChartTypeEnum.Pie,
];

export const isBarChart = (type: AssistantChartTypeEnum): boolean =>
  type === AssistantChartTypeEnum.Bar ||
  type === AssistantChartTypeEnum.StackedBar;

/**
 * True when the chart can be drawn: a known type, at least one series and one point, and
 * no more series (or pie slices) than there are palette colors.
 * @registry Guards a Scout chart spec before it is drawn on screen or in print.
 */
export const isDisplayableChart = (chart: AssistantChartResponse): boolean => {
  const seriesCount = chart.series?.length ?? 0;
  const pointCount = chart.points?.length ?? 0;
  const colorsNeeded =
    chart.type === AssistantChartTypeEnum.Pie ? pointCount : seriesCount;
  return (
    CHART_TYPES.includes(chart.type) &&
    seriesCount > 0 &&
    pointCount > 0 &&
    colorsNeeded <= CHAT_CHART_COLORS.length
  );
};

/** The value of series `seriesIndex` at a point, or null when missing or not finite. */
export const chartValueAt = (
  point: AssistantChartPointResponse,
  seriesIndex: number
): number | null => {
  const value: unknown = point.values?.[seriesIndex];
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
};

/** The slices a pie can draw: a zero, negative, or missing value has no area. */
export const drawablePieSlices = <T extends {value: number | null}>(
  slices: T[]
): (T & {value: number})[] =>
  slices.filter(
    (slice): slice is T & {value: number} =>
      slice.value !== null && slice.value > 0
  );

// Series labels are model-written, so data keys are synthetic and never collide.
export const CHART_X_KEY = 'x';
export const chartSeriesKey = (index: number): string => `s${index}`;

/** One recharts data row per point, keyed by `CHART_X_KEY` and `chartSeriesKey`. */
export const chartDataRows = (
  chart: AssistantChartResponse
): Record<string, string | number | null>[] =>
  chart.points.map(point => {
    const row: Record<string, string | number | null> = {
      [CHART_X_KEY]: point.x,
    };
    chart.series.forEach((_, index) => {
      row[chartSeriesKey(index)] = chartValueAt(point, index);
    });
    return row;
  });

const countFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 0,
});
const decimalFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 2,
});
const percentFormatter = new Intl.NumberFormat('en-US', {
  maximumFractionDigits: 1,
});
const currencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const compactFormatter = new Intl.NumberFormat('en-US', {
  notation: 'compact',
  maximumFractionDigits: 1,
});
const compactCurrencyFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

/**
 * Formats a chart value by the chart unit: currency as USD with cents, percent as an
 * already-scaled number (42.5 → "42.5%"), hours with an "h" suffix, count as an integer.
 * @registry Unit-aware value formatter for Scout charts (tooltip, data table, export).
 */
export const formatChartValue = (
  value: number | null | undefined,
  unit: AssistantChartUnitEnum
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return CHART_EMPTY_VALUE;
  }
  switch (unit) {
    case AssistantChartUnitEnum.Currency:
      return currencyFormatter.format(value);
    case AssistantChartUnitEnum.Percent:
      return `${percentFormatter.format(value)}%`;
    case AssistantChartUnitEnum.Hours:
      return `${decimalFormatter.format(value)} h`;
    case AssistantChartUnitEnum.Count:
      return countFormatter.format(value);
    default:
      return decimalFormatter.format(value);
  }
};

/** Short value-axis tick label ("$1.2K", "1.5K h", "40%") that fits a phone-width axis. */
export const formatChartAxisTick = (
  value: number,
  unit: AssistantChartUnitEnum
): string => {
  switch (unit) {
    case AssistantChartUnitEnum.Currency:
      return compactCurrencyFormatter.format(value);
    case AssistantChartUnitEnum.Percent:
      return `${compactFormatter.format(value)}%`;
    case AssistantChartUnitEnum.Hours:
      return `${compactFormatter.format(value)} h`;
    default:
      return compactFormatter.format(value);
  }
};

export interface ChartDataTable {
  header: string[];
  rows: string[][];
}

/** A chart's data as display strings: the x label, then one column per series. Null when empty. */
export const chartDataTable = (
  chart: AssistantChartResponse
): ChartDataTable | null => {
  const series = chart.series ?? [];
  const points = chart.points ?? [];
  if (series.length === 0 || points.length === 0) return null;
  return {
    header: [chart.xLabel, ...series.map(s => s.label)],
    rows: points.map(point => [
      point.x,
      ...series.map((_, index) =>
        formatChartValue(chartValueAt(point, index), chart.unit)
      ),
    ]),
  };
};

const CHART_HEIGHT = 240;
const MIN_ROWS_CHART_HEIGHT = 120;
const MAX_COLUMN_POINTS = 6;
const LONG_LABEL_CHARS = 12;
const ROW_HEIGHT = 28;
const MIN_ROW_HEIGHT = 18;
const ROWS_COMFORT_HEIGHT = 900;
const MAX_LABEL_CHARS = 16;
const MIN_LABEL_CHARS = 6;
const LABEL_WIDTH_SHARE = 0.4;
// Estimates for a 12px tick: recharts cannot be measured before render.
const LABEL_CHAR_PX = 8;
export const TICK_FONT_PX = 12;
const CATEGORY_TICK_GAP_PX = 20;
const TILTED_LABEL_EDGE_PX = 8;
// Recharts defaults: the X axis is 30px tall and the Y axis 60px wide.
export const DEFAULT_X_AXIS_HEIGHT = 30;
const DEFAULT_Y_AXIS_WIDTH = 60;
export const LEGEND_HEIGHT = 36;
export const CHART_VERTICAL_MARGIN = 10;
const MAX_TOOLTIP_WIDTH = 220;
export const MIN_TOOLTIP_WIDTH = 120;
export const TILTED_LABEL_ANGLE = -35;

/**
 * `rows` draws horizontal bars with categories down the Y axis, `columns` draws them along
 * the X axis; `tooltipMaxWidth` keeps the tooltip inside the plot area.
 */
export type ChatChartLayout =
  | {
      kind: 'rows';
      height: number;
      categoryAxisWidth: number;
      labelMaxChars: number;
      integerTicks: boolean;
      tooltipMaxWidth: number;
    }
  | {
      kind: 'columns';
      height: number;
      tilt: {axisHeight: number; leftMargin: number; labelMaxChars: number} | null;
      integerTicks: boolean;
      tooltipMaxWidth: number;
    };

const hasOnlyIntegerValues = (chart: AssistantChartResponse): boolean =>
  (chart.points ?? []).every(point =>
    (chart.series ?? []).every((_, index) => {
      const value = chartValueAt(point, index);
      return value === null || Number.isInteger(value);
    })
  );

const labelCharsWithin = (budgetPx: number, pxPerChar: number): number =>
  Math.max(
    MIN_LABEL_CHARS,
    Math.min(MAX_LABEL_CHARS, Math.floor(budgetPx / pxPerChar))
  );

/**
 * Bar charts with more than 6 categories or a label over 12 characters switch to horizontal
 * bars; line and area charts tilt long X labels. `availableWidth` 0 means not yet measured.
 */
export const chooseChartLayout = (
  chart: AssistantChartResponse,
  availableWidth = 0
): ChatChartLayout => {
  const points = chart.points ?? [];
  const longestLabel = points.reduce(
    (longest, point) => Math.max(longest, (point.x ?? '').length),
    0
  );
  const integerTicks =
    chart.unit === AssistantChartUnitEnum.Count || hasOnlyIntegerValues(chart);
  const legendHeight = (chart.series?.length ?? 0) >= 2 ? LEGEND_HEIGHT : 0;
  const isBar = isBarChart(chart.type);
  const measured = availableWidth > 0;
  const labelBudgetPx = measured
    ? availableWidth * LABEL_WIDTH_SHARE
    : Number.POSITIVE_INFINITY;
  const tooltipMaxWidth = (plotLeft: number): number =>
    measured
      ? Math.max(
          MIN_TOOLTIP_WIDTH,
          Math.min(MAX_TOOLTIP_WIDTH, Math.floor(availableWidth - plotLeft))
        )
      : MAX_TOOLTIP_WIDTH;

  if (
    isBar &&
    (points.length > MAX_COLUMN_POINTS || longestLabel > LONG_LABEL_CHARS)
  ) {
    const rowsHeight = Math.max(
      points.length * MIN_ROW_HEIGHT,
      Math.min(points.length * ROW_HEIGHT, ROWS_COMFORT_HEIGHT)
    );
    const labelMaxChars = labelCharsWithin(
      labelBudgetPx - CATEGORY_TICK_GAP_PX,
      LABEL_CHAR_PX
    );
    const categoryAxisWidth =
      Math.min(longestLabel, labelMaxChars) * LABEL_CHAR_PX +
      CATEGORY_TICK_GAP_PX;
    return {
      kind: 'rows',
      height: Math.max(
        MIN_ROWS_CHART_HEIGHT,
        rowsHeight + DEFAULT_X_AXIS_HEIGHT + CHART_VERTICAL_MARGIN + legendHeight
      ),
      categoryAxisWidth,
      labelMaxChars,
      integerTicks,
      tooltipMaxWidth: tooltipMaxWidth(categoryAxisWidth),
    };
  }

  if (isBar || longestLabel <= LONG_LABEL_CHARS) {
    return {
      kind: 'columns',
      height: CHART_HEIGHT,
      tilt: null,
      integerTicks,
      tooltipMaxWidth: tooltipMaxWidth(DEFAULT_Y_AXIS_WIDTH),
    };
  }

  const angle = (Math.abs(TILTED_LABEL_ANGLE) * Math.PI) / 180;
  const labelMaxChars = labelCharsWithin(
    labelBudgetPx,
    LABEL_CHAR_PX * Math.cos(angle)
  );
  const labelPx = Math.min(longestLabel, labelMaxChars) * LABEL_CHAR_PX;
  const axisHeight = Math.ceil(
    labelPx * Math.sin(angle) + TICK_FONT_PX * Math.cos(angle) + TICK_FONT_PX
  );
  const leftMargin = Math.max(
    0,
    Math.ceil(labelPx * Math.cos(angle)) +
      TILTED_LABEL_EDGE_PX -
      DEFAULT_Y_AXIS_WIDTH
  );
  return {
    kind: 'columns',
    height: CHART_HEIGHT + axisHeight - DEFAULT_X_AXIS_HEIGHT,
    tilt: {axisHeight, leftMargin, labelMaxChars},
    integerTicks,
    tooltipMaxWidth: tooltipMaxWidth(DEFAULT_Y_AXIS_WIDTH + leftMargin),
  };
};

/** Cuts a category label to `maxChars`, ending with an ellipsis when shortened. */
export const truncateChartLabel = (label: string, maxChars: number): string =>
  label.length > maxChars
    ? `${label.slice(0, maxChars - 1).trimEnd()}…`
    : label;
